import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { logError } from '@zg-rentals/log-error';
import { bootstrap } from '../bootstrap';
import { WEBSOCKET_DEBUG } from './useWebsocketFeature';
import { AppContext } from '../contexts/AppContextProvider';
import useWindowSize from './useWindowSize';
import { useAuth } from '../AuthProvider';

const logger = bootstrap.logger.child({ name: 'useWebSocketConnection' });

// Event structure from the WebSocket messages
type WebSocketEvent = {
  type: 'conversationUpdated' | 'newConversation' | 'newMessage';
  conversationId?: string;
  messageId?: string;
  createdAtMs?: number;
  lastMessageAtMs?: number;
};

// WebSocket message format
export type WebSocketMessage = {
  topic?: string;
  event?: WebSocketEvent;
  v?: number;
  ts?: number;
  id?: string;
  conversationId?: string;
};

type AuthenticationResponse = {
  type: 'id';
  userToken: string;
  sessionId: string;
};

type SubscriptionRequest = {
  command: 'subscribe' | 'unsubscribe';
  topic: string;
};

type WebSocketMessageData = AuthenticationResponse | WebSocketMessage | unknown;

type UseWebSocketConnection = {
  onNewMessage?: (message: WebSocketMessage) => void | Promise<void>;
  isEnabled?: boolean;
  topicSuffixes?: Array<string>; // Array of topic suffixes to subscribe to
};

const logDebug = (message: string, data?: Record<string, unknown>) => {
  if (WEBSOCKET_DEBUG) {
    if (data) {
      logger.info(message, data);
    } else {
      logger.info(message);
    }
  }
};

const safeJsonParse = (data: string) => {
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

// Configuration
const RECONNECT_INTERVAL = 3 * 1000; // 3 seconds in ms
const MAX_RECONNECT_INTERVAL = 5 * 60 * 1000; // 5 minutes in ms
const MAX_RECONNECT_ATTEMPTS = 15;

const DEFAULT_TOPIC_SUFFIXES = ['|inbox:renter'];

// WebSocket close code descriptions for better debugging
const getWebSocketCloseCodeDescription = (code: number): string => {
  const descriptions: Record<number, string> = {
    1000: 'Normal closure',
    1001: 'Going away (page unload)',
    1002: 'Protocol error',
    1003: 'Unsupported data type',
    1004: 'Reserved',
    1005: 'No status code',
    1006: 'Abnormal closure (network issue)', // Very common on mobile
    1007: 'Invalid data',
    1008: 'Policy violation',
    1009: 'Message too large',
    1010: 'Extension required',
    1011: 'Internal server error',
    1012: 'Service restart',
    1013: 'Try again later',
    1014: 'Bad gateway',
    1015: 'TLS handshake failure',
  };

  return descriptions[code] || `Unknown code ${code}`;
};

// Get brand-specific WebSocket URL
const getBrandSpecificWebSocketURL = (brand?: string): string => {
  // If environment variable is set, use it (for custom configurations)
  if (process.env.NEXT_PUBLIC_WEBSOCKET_URL) {
    return process.env.NEXT_PUBLIC_WEBSOCKET_URL;
  }

  // Use Zillow WebSocket for Zillow brand, HotPads for everything else
  if (brand === 'zillow') {
    return 'wss://www.zillow.com/rental/satellite/ws/services';
  }

  return 'wss://hotpads.com/rental/satellite/ws/services';
};

export const useWebSocketConnection = (options: UseWebSocketConnection = {}) => {
  const { onNewMessage, isEnabled = true, topicSuffixes = DEFAULT_TOPIC_SUFFIXES } = options;
  const { brand, isMobileDevice } = useContext(AppContext);
  const { isMobileView } = useWindowSize();

  const { user, isAuthorized } = useAuth();

  // Only enable WebSocket if feature is enabled AND user is authenticated
  const shouldConnectWebSocket = isEnabled && isAuthorized;

  // Debug logging for authentication state
  useEffect(() => {
    if (WEBSOCKET_DEBUG) {
      logDebug('WebSocket authentication check', {
        isEnabled,
        isAuthorized,
        shouldConnectWebSocket,
        userLoggedIn: user?.isLoggedIn,
        hasUserToken: !!user?.userToken,
      });
    }
  }, [isEnabled, isAuthorized, shouldConnectWebSocket, user?.isLoggedIn, user?.userToken]);

  const websocketUrl = useMemo(() => getBrandSpecificWebSocketURL(brand), [brand]);
  const [authenticatedUserToken, setAuthenticatedUserToken] = useState<string | null>(null);
  const [hadErrorSinceLastOpen, setHadErrorSinceLastOpen] = useState(false);
  const subscribedTopicsRef = useRef<Set<string>>(new Set());
  const hadErrorSinceLastOpenRef = useRef(false);

  const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket<WebSocketMessageData>(
    websocketUrl,
    {
      shouldReconnect: (closeEvent: CloseEvent) => {
        const shouldReconnect = Boolean(shouldConnectWebSocket);

        if (shouldReconnect) {
          logger.info('WebSocket reconnection attempt', {
            code: closeEvent.code,
            reason: closeEvent.reason,
            wasClean: closeEvent.wasClean,
            isMobileDevice,
            isMobileView,
          });
        }

        return shouldReconnect;
      },
      reconnectAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectInterval: (attempt) => {
        const exp = RECONNECT_INTERVAL * Math.pow(2, attempt); // Exponential backoff
        const jitter = exp * (0.85 + Math.random() * 0.15); // Add jitter +/- 15%
        return Math.min(jitter, MAX_RECONNECT_INTERVAL); // Cap at 5 minutes
      },
      onOpen: handleWebSocketOpen,
      onError: handleWebSocketError,
      onClose: handleWebSocketClose,
    },
    Boolean(shouldConnectWebSocket), // Only connect if user is authenticated
  );

  const isConnecting = shouldConnectWebSocket && readyState === ReadyState.CONNECTING;
  const isWebSocketEnabledAndReady =
    shouldConnectWebSocket && readyState === ReadyState.OPEN && !!authenticatedUserToken;

  // WebSocket event handlers
  function handleWebSocketOpen() {
    const ws = getWebSocket();
    logDebug('WebSocket connected successfully', {
      readyState: ws?.readyState,
      url: ws?.url,
    });

    if (hadErrorSinceLastOpenRef.current) {
      setHadErrorSinceLastOpen(true);
      hadErrorSinceLastOpenRef.current = false;
    }

    authenticateUser();
  }

  function handleWebSocketError(error: Event) {
    // Error is handled in handleWebSocketClose to avoid duplicate error logs
    logDebug('WebSocket error event received:', {
      type: error.type,
      readyState: getWebSocket()?.readyState,
    });
  }

  function handleWebSocketClose(closeEvent: CloseEvent) {
    const code = closeEvent.code;
    const isAuthError = code === 1008 || code === 1015;
    const isServerError = code === 1011 || code === 1002 || code === 1003;
    const isNetworkError = code === 1006;

    const closeDetails = {
      code,
      reason: closeEvent.reason,
      wasClean: closeEvent.wasClean,
      isMobileDevice,
      isMobileView,
      codeDescription: getWebSocketCloseCodeDescription(code),
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
      connectionType:
        typeof navigator !== 'undefined' && 'connection' in navigator
          ? (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType
          : 'unknown',
    };

    if (isAuthError || isServerError || isNetworkError) {
      logError({
        error: closeEvent,
        context: {
          message: isNetworkError
            ? 'WebSocket closed due to network issue'
            : 'WebSocket closed due to server/policy/TLS error',
          ...closeDetails,
        },
      });
    } else {
      logger.info('WebSocket closed', closeDetails);
    }

    hadErrorSinceLastOpenRef.current = true;
    resetAuthentication();
  }

  // Authentication functions
  const authenticateUser = useCallback(() => {
    const ws = getWebSocket();
    const open = ws?.readyState === WebSocket.OPEN;
    if (!open) {
      logDebug('Skipping auth attempt, socket not yet connected');
      return;
    }
    logDebug('Authenticating user - Requesting userToken');
    sendMessage('id|');
  }, [sendMessage, getWebSocket]);

  const resetAuthentication = useCallback(() => {
    setAuthenticatedUserToken(null);
    subscribedTopicsRef.current.clear();
  }, []);

  const handleAuthenticationResponse = useCallback(
    (authResponse: AuthenticationResponse) => {
      if (authenticatedUserToken !== authResponse.userToken) {
        logDebug('Got authenticated userToken', { authResponse });
        setAuthenticatedUserToken(authResponse.userToken);
      }
    },
    [authenticatedUserToken],
  );

  // Topic subscription management
  useEffect(() => {
    if (!shouldConnectWebSocket || !authenticatedUserToken || readyState !== ReadyState.OPEN) return;

    // Build desired topics
    const desired = new Set(topicSuffixes.map((s) => `${authenticatedUserToken}${s}`));
    const prev = subscribedTopicsRef.current;

    // Unsubscribe from topics no longer desired
    prev.forEach((topic) => {
      if (!desired.has(topic)) {
        const req: SubscriptionRequest = { command: 'unsubscribe', topic };
        const frame = `websocketSubscription|${JSON.stringify(req)}`;
        logDebug('Unsubscribing from topic', { topic });
        sendMessage(frame);
        prev.delete(topic);
      }
    });

    // Subscribe to newly desired topics
    desired.forEach((topic) => {
      if (!prev.has(topic)) {
        const req: SubscriptionRequest = { command: 'subscribe', topic };
        const frame = `websocketSubscription|${JSON.stringify(req)}`;
        logDebug('Subscribing to topic', { topic });
        sendMessage(frame);
        prev.add(topic);
      }
    });
  }, [shouldConnectWebSocket, authenticatedUserToken, topicSuffixes, sendMessage, readyState]);

  // Message processing
  const handlePushNotification = useCallback(
    (messageData: WebSocketMessage) => {
      logDebug('Push notification received:', messageData);

      if (messageData.event) {
        logDebug('Event details:', {
          type: messageData.event.type,
          conversationId: messageData.event.conversationId,
          messageId: messageData.event.messageId,
        });
      }

      if (onNewMessage) {
        onNewMessage(messageData);
      }
    },
    [onNewMessage],
  );

  const processWebSocketMessage = useCallback(
    (messageData: WebSocketMessageData) => {
      if (messageData && typeof messageData === 'object' && 'type' in messageData && messageData.type === 'id') {
        handleAuthenticationResponse(messageData as AuthenticationResponse);
        return;
      }

      // Handle topic-based messages with event structure
      if (
        messageData &&
        typeof messageData === 'object' &&
        ('event' in messageData || 'conversationId' in messageData)
      ) {
        handlePushNotification(messageData as WebSocketMessage);
        return;
      }

      logError({
        error: 'Received non-handled WebSocket message',
        context: typeof messageData === 'object' ? JSON.stringify(messageData) : String(messageData),
      });
    },
    [handleAuthenticationResponse, handlePushNotification],
  );

  // Main message processing effect
  useEffect(() => {
    if (!shouldConnectWebSocket || !lastMessage) return;

    try {
      const messageData = safeJsonParse(lastMessage.data);
      processWebSocketMessage(messageData);
    } catch (error) {
      logError({ error, context: 'Failed to parse WebSocket message' });
    }
  }, [shouldConnectWebSocket, lastMessage, processWebSocketMessage]);

  // Auto-reset the one-shot recovery flag after consumers react to it
  useEffect(() => {
    if (!isWebSocketEnabledAndReady || !hadErrorSinceLastOpen) return;

    const timer = setTimeout(() => {
      setHadErrorSinceLastOpen(false);
    }, 0);

    return () => clearTimeout(timer);
  }, [isWebSocketEnabledAndReady, hadErrorSinceLastOpen]);

  return {
    hadErrorSinceLastOpen,
    isConnecting,
    isWebSocketEnabledAndReady,
    connectionStatus: readyState,
    lastMessage: lastMessage ? safeJsonParse(lastMessage.data) : null,
    authenticatedUserToken,
  };
};
